import { MODULE_NAME } from './const';
import StoreModuleService from '../StoreModuleService';
import { SET_TAGS, SET_IDEAS, SET_IDEA, SET_COMMENTS, SET_COMMENT, DELETE_COMMENT, SET_FOCUS_ON_IDEA } from './mutations';
import isEmpty from 'lodash/isEmpty';
import api from '@/api';
import { STATUS_CODES } from '@/utils/error-handling-service';
import { WORKSPACE_TYPE } from '@/components/InspirationCenter/inspiration-center.const.js';

const buildInspirationCenterActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);
// actions declarations
export const getInspirationTags = buildInspirationCenterActionObject('getInspirationTags');
export const getInspirationIdeas = buildInspirationCenterActionObject('getInspirationIdeas');
export const getIdea = buildInspirationCenterActionObject('getIdea');
export const saveIdeaToAccount = buildInspirationCenterActionObject('saveIdeaToAccount');
export const voteForIdea = buildInspirationCenterActionObject('voteForIdea');
export const unvoteForIdea = buildInspirationCenterActionObject('unvoteForIdea');
export const getIdeaComments = buildInspirationCenterActionObject('getIdeaComments');
export const addIdeaComment = buildInspirationCenterActionObject('addIdeaComment');
export const getIdeaComment = buildInspirationCenterActionObject('getIdeaComment');
export const updateIdeaComment = buildInspirationCenterActionObject('updateIdeaComment');
export const removeIdeaComment = buildInspirationCenterActionObject('removeIdeaComment');
export const likeIdeaComment = buildInspirationCenterActionObject('likeIdeaComment');
export const unlikeIdeaComment = buildInspirationCenterActionObject('unlikeIdeaComment');
export const getMyTeamIdeas = buildInspirationCenterActionObject('getMyTeamIdeas');
export const archiveIdea = buildInspirationCenterActionObject('archiveIdea');
export const createIdea = buildInspirationCenterActionObject('createIdea');
export const clearFocusOnIdea = buildInspirationCenterActionObject('clearFocusOnIdea');
export const createIdeaPusherConnection = buildInspirationCenterActionObject('createIdeaPusherConnection');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getInspirationTags.action]({ commit, state }, payload) {
		const ideas = state.tags;
		const isDataMissing = isEmpty(ideas);
		const needToGet = isDataMissing || payload?.forceGet;
		if (needToGet) {
			const res = await api.getInspirationTags();
			commit(SET_TAGS, res);
		}
	},
	async [getInspirationIdeas.action]({ commit, state }, payload) {
		const res = await api.getInspirationIdeas(payload.page, payload.tags, payload.sortByWorkspaceFirst, payload.type);
		const newIdeasHashmap = Object.fromEntries(res.results.map((idea) => [idea.id, idea]));
		commit(SET_IDEAS, { ...state.ideas, ...newIdeasHashmap });
		return res;
	},
	async [getIdea.action]({ commit }, { ideaId }) {
		const res = await api.getIdea(ideaId);
		commit(SET_IDEA, res);
		return res;
	},
	[getMyTeamIdeas.action]({ dispatch }) {
		return dispatch(getInspirationIdeas.action, { page: 1, type: WORKSPACE_TYPE });
	},
	async [saveIdeaToAccount.action]({ commit }, { ideaId, focusOnIdea = false }) {
		const res = await api.saveIdeaToAccount(ideaId);
		commit(SET_IDEA, res);
		if (focusOnIdea) {
			commit(SET_FOCUS_ON_IDEA, { ideaId });
		}
		return res;
	},
	async [voteForIdea.action]({ commit }, { ideaId }) {
		const res = await api.voteForIdea(ideaId);
		commit(SET_IDEA, res);
		return res;
	},
	async [unvoteForIdea.action]({ commit }, { ideaId }) {
		const res = await api.unvoteForIdea(ideaId);
		commit(SET_IDEA, res);
		return res;
	},
	async [getIdeaComments.action]({ commit, state }, { ideaId, page }) {
		const allComments = state.comments;
		const res = await api.getIdeaComments(ideaId, page);
		const newCommentsHashmap = Object.fromEntries(res.results.map((comment) => [comment.id, comment]));
		commit(SET_COMMENTS, { ...allComments, ...newCommentsHashmap });
		return res;
	},
	async [addIdeaComment.action]({ commit, dispatch }, { ideaId, content }) {
		const res = await api.addIdeaComment(ideaId, content);
		commit(SET_COMMENT, res);
		dispatch(getIdea.action, { ideaId });
		return res;
	},
	async [getIdeaComment.action]({ commit }, { ideaId, commentId }) {
		let res;
		try {
			res = await api.getIdeaComment(ideaId, commentId);
			commit(SET_COMMENT, res);
			return res;
		} catch (err) {
			if (err?.request?.status === STATUS_CODES.NOT_FOUND) {
				commit(DELETE_COMMENT, { commentId });
			}
		}
	},
	async [updateIdeaComment.action]({ commit }, { ideaId, commentId, content }) {
		const res = await api.updateIdeaComment(ideaId, commentId, content);
		commit(SET_COMMENT, res);
		return res;
	},
	async [removeIdeaComment.action]({ commit }, { ideaId, commentId, skipRequest = false }) {
		let res;
		if (!skipRequest) {
			res = await api.deleteIdeaComment(ideaId, commentId);
		}
		commit(DELETE_COMMENT, { commentId });
		return res;
	},
	async [likeIdeaComment.action]({ commit }, { ideaId, commentId }) {
		const res = await api.likeIdeaComment(ideaId, commentId);
		commit(SET_COMMENT, res);
		return res;
	},
	async [unlikeIdeaComment.action]({ commit }, { ideaId, commentId }) {
		const res = await api.unlikeIdeaComment(ideaId, commentId);
		commit(SET_COMMENT, res);
		return res;
	},
	async [archiveIdea.action]({ commit }, { ideaId }) {
		const res = await api.archiveIdea(ideaId);
		commit(SET_IDEA, res);
		return res;
	},
	async [createIdea.action]({ commit }, payload) {
		const res = await api.createIdea(payload.params);
		commit(SET_FOCUS_ON_IDEA, { ideaId: res.id });
		commit(SET_IDEA, res);
		return res;
	},
	[clearFocusOnIdea.action]({ commit }) {
		commit(SET_FOCUS_ON_IDEA, { ideaId: null });
	},
};

export default actions;
